// TenantPolicy, EdgePolicy and the edge projection (E01-S6-T1; contracts 1.0 section 11).
//
// A policy can only tighten: the autonomy cap has no "commit" member (rule 10), a class override is
// mask or drop and never a loosening word, and limits may only lower the platform's (D-16).
import { z } from "zod";
import { Count, Id, NonEmpty, PerceptionTier, strictObject, version } from "./primitives.js";
import { ContextClass } from "./model.js";
/** How far the assistant may go. "commit" is not a value and nothing may widen the list (rule 10). */
export const AutonomyCap = z.enum(["observe", "explain", "point", "navigate", "prepare"]);
/** A policy can tighten a class and cannot loosen one. */
export const ClassOverride = z.enum(["mask", "drop"]);
export const Rehydrate = z.enum(["off", "identifiers"]);
/** The six retention categories of D-74. "transcripts" is prompts and outputs together, not a seventh. */
export const RETENTION_CATEGORIES = [
    "prompts",
    "outputs",
    "frames",
    "telemetry",
    "records",
    "audit",
];
export const RetentionCategory = z.enum(RETENTION_CATEGORIES);
/**
 * The audit log's retention floor (D-74 says there is one and leaves the number open). 365 days: a
 * year of audit is the least a regulated firm can account for, and the floor also forbids "none".
 */
export const AUDIT_RETENTION_FLOOR_DAYS = 365;
/**
 * Days kept, "none" (never stored) or "client-archive". The type carries "client-archive" as the
 * contract does; validation rejects it until it arrives with the records export in the pilot (D-74).
 */
export const RetentionRule = strictObject({
    days: z
        .union([Count, z.literal("none"), z.literal("client-archive")])
        .refine((d) => d !== "client-archive", "client-archive is not available until the pilot (D-74)"),
});
const PositiveInt = z.number().int().positive();
export const TenantPolicy = strictObject({
    v: version(1),
    version: PositiveInt,
    perception: strictObject({
        maxTier: PerceptionTier,
        rehydrate: Rehydrate,
        classOverrides: z.record(NonEmpty, ClassOverride).optional(),
        excludedScreens: z.array(Id).optional(),
    }),
    egress: strictObject({
        rules: z.array(strictObject({
            classes: z.array(ContextClass).min(1),
            to: z.enum(["any", "private-only", "none"]),
        })),
    }),
    autonomy: strictObject({ cap: AutonomyCap }),
    retention: z.record(RetentionCategory, RetentionRule).superRefine((r, ctx) => {
        const days = r.audit.days;
        if (typeof days !== "number" || days < AUDIT_RETENTION_FLOOR_DAYS) {
            ctx.addIssue({
                code: "custom",
                path: ["audit", "days"],
                message: `audit retention must be at least ${String(AUDIT_RETENTION_FLOOR_DAYS)} days (D-74)`,
            });
        }
    }),
    voice: strictObject({ enabled: z.boolean(), sttLocalOnly: z.boolean() }),
    analytics: strictObject({
        individualView: z.enum(["off", "self-only", "manager"]),
        minGroupSize: PositiveInt,
    }),
    limits: strictObject({
        utterancesPerMinute: PositiveInt,
        framesPerSecond: PositiveInt,
        modelCallsPerMinute: PositiveInt,
    }),
    disclosure: strictObject({ everySession: z.literal(true) }),
});
/** The tenant policy projected for one user: only what the edge needs to behave (D-48). */
export const EdgePolicy = strictObject({
    tier: PerceptionTier,
    rehydrate: Rehydrate,
    classOverrides: z.record(NonEmpty, ClassOverride),
    excludedScreens: z.array(Id),
    autonomyCap: AutonomyCap,
    voice: z.boolean(),
    framesPerSecond: PositiveInt,
    transcriptsStored: z.boolean(),
});
/**
 * Pure. The tier is the lower of the policy's and the client's managed ceiling; the field's own
 * minimum applies later, per field. Transcripts are prompts and outputs together (D-74), so they are
 * stored only when neither is "none". Egress, retention and analytics never reach the edge.
 */
export function projectForEdge(policy, opts) {
    const ceiling = opts?.clientCeiling ?? 3;
    const fps = opts?.platform
        ? Math.min(policy.limits.framesPerSecond, opts.platform.framesPerSecond)
        : policy.limits.framesPerSecond; // D-16: a tenant never raises a limit above the platform's
    return {
        tier: Math.min(policy.perception.maxTier, ceiling),
        rehydrate: policy.perception.rehydrate,
        classOverrides: { ...(policy.perception.classOverrides ?? {}) },
        excludedScreens: [...(policy.perception.excludedScreens ?? [])],
        autonomyCap: policy.autonomy.cap,
        voice: policy.voice.enabled,
        framesPerSecond: fps,
        // stored if EITHER side of the conversation is kept: the user is told the truth about their own words (D-74)
        transcriptsStored: policy.retention.prompts.days !== "none" || policy.retention.outputs.days !== "none",
    };
}
/**
 * The policy in force when none is supplied (section 11): tier 0, autonomy observe, voice off.
 * Deny by default (rule 3): no egress rule, so no class leaves; nothing said is stored.
 */
export const FLOOR_POLICY = deepFreeze({
    v: 1,
    version: 1,
    perception: { maxTier: 0, rehydrate: "off" },
    egress: { rules: [] },
    autonomy: { cap: "observe" },
    retention: {
        prompts: { days: "none" },
        outputs: { days: "none" },
        frames: { days: "none" },
        telemetry: { days: 90 },
        records: { days: 365 },
        audit: { days: AUDIT_RETENTION_FLOOR_DAYS },
    },
    voice: { enabled: false, sttLocalOnly: true },
    analytics: { individualView: "off", minGroupSize: 5 },
    limits: { utterancesPerMinute: 20, framesPerSecond: 4, modelCallsPerMinute: 30 },
    disclosure: { everySession: true },
});
function deepFreeze(value) {
    if (value && typeof value === "object") {
        for (const v of Object.values(value))
            deepFreeze(v);
        Object.freeze(value);
    }
    return value;
}
//# sourceMappingURL=policy.js.map